/**
 * R7 — Pending approval (design/redesign_v3/Auth & Onboarding.dc.html frames
 * "Pending approval" + "Pending desktop"). Static explanation while the
 * society admin reviews the signup, a 5%-opacity watermark arch in the
 * corner, and a summary card of what was submitted. Sign-out clears any
 * local session and returns to /login. Desktop (≥1024): header row with
 * account email + sign out, wider centered content.
 */
import { router, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { View, useWindowDimensions } from 'react-native';

import { space } from '@bero/tokens';
import { BeroMark, BrandLockup } from '@/components/brand';
import { fmtTime } from '@/components/gate/api';
import { Icon } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { AppText, Button, Screen } from '@/ui';

const ONBOARDING_DESKTOP_BREAKPOINT = 1024;

function initialsOf(text: string): string {
  const words = text.split(/[\s·-]+/).filter(Boolean);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? words[0]?.[1] ?? '')).toUpperCase();
}

function submittedLabel(ms: string | undefined): string | null {
  if (!ms) return null;
  const d = new Date(Number(ms));
  if (Number.isNaN(d.getTime())) return null;
  return `${d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}, ${fmtTime(d)}`;
}

/** 5%-opacity watermark arch, bottom-right — matches the Pending frames. */
function WatermarkArch({ size = 300 }: { size?: number }) {
  return (
    <View pointerEvents="none" style={{ position: 'absolute', right: -size * 0.22, bottom: -size * 0.18, opacity: 0.05 }}>
      <BeroMark height={size} />
    </View>
  );
}

function SummaryCard({ flatCode, societyName, residentType, submittedAt }: {
  flatCode?: string; societyName?: string; residentType?: string; submittedAt?: string;
}) {
  const { colors } = useTheme();
  const { t } = useT();
  const title = [flatCode, societyName].filter(Boolean).join(' · ');
  if (!title) return null;
  const when = submittedLabel(submittedAt);
  const roleLabel = residentType === 'TENANT' ? t('onb_role_tenant') : residentType === 'FAMILY' ? t('onb_role_family') : t('onb_role_owner');
  return (
    <View style={{
      marginTop: space.s6, width: '100%', maxWidth: 460, borderWidth: 1, borderColor: colors.borderSoft,
      borderRadius: 16, backgroundColor: colors.canvas1, padding: space.s4,
      flexDirection: 'row', alignItems: 'center', gap: space.s3,
    }}>
      <View style={{
        width: 44, height: 50, borderRadius: 6, borderTopLeftRadius: 22, borderTopRightRadius: 22,
        backgroundColor: colors.primary100, alignItems: 'center', justifyContent: 'center',
      }}>
        <AppText weight="700" style={{ color: colors.primary700, fontSize: 14 }}>{initialsOf(title)}</AppText>
      </View>
      <View style={{ flex: 1 }}>
        <AppText variant="bodyMd" weight="600">{title}</AppText>
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
          {roleLabel}{when ? ` · ${t('onb_pending_submitted', { when })}` : ''}
        </AppText>
      </View>
      <View style={{ backgroundColor: colors.warning50, borderRadius: 999, paddingHorizontal: 10, paddingVertical: 5 }}>
        <AppText variant="label" style={{ color: colors.warning500 }}>{t('onb_pending_badge')}</AppText>
      </View>
    </View>
  );
}

export default function OnboardingPendingScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { width } = useWindowDimensions();
  const { user, signOut } = useSession();
  const { flatCode, societyName, residentType, submittedAt } = useLocalSearchParams<{
    flatCode?: string; societyName?: string; residentType?: string; submittedAt?: string;
  }>();
  const [signingOut, setSigningOut] = React.useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
    } catch {
      // best effort — proceed to sign-in regardless
    } finally {
      router.replace('/login');
    }
  };

  const resolvedFlatCode = flatCode ?? user?.flat?.code;
  const desktop = width >= ONBOARDING_DESKTOP_BREAKPOINT;

  return (
    <Screen scroll={false} safeTop style={{ position: 'relative', overflow: 'hidden' }}>
      <WatermarkArch size={desktop ? 480 : 300} />
      <View style={{
        flexDirection: 'row', alignItems: 'center', gap: 10,
        paddingHorizontal: desktop ? space.s10 : space.s4, paddingVertical: space.s4,
        borderBottomWidth: 1, borderBottomColor: colors.borderSoft,
      }}>
        <BrandLockup markHeight={desktop ? 26 : 20} />
        {desktop ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 'auto', gap: space.s3 }}>
            {user?.email ? <AppText variant="bodySm" tone="tertiary">{user.email}</AppText> : null}
            <AppText
              variant="bodySm"
              weight="500"
              onPress={signingOut ? undefined : () => void handleSignOut()}
              style={{ color: colors.primary500 }}
            >
              {t('onb_sign_out')}
            </AppText>
          </View>
        ) : null}
      </View>

      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: space.s8 }}>
        <View style={{
          width: desktop ? 96 : 88, height: desktop ? 110 : 100, borderRadius: 6,
          borderTopLeftRadius: desktop ? 48 : 44, borderTopRightRadius: desktop ? 48 : 44,
          backgroundColor: colors.warning50, alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name="clock" size={28} color={colors.warning500} />
        </View>
        <AppText variant={desktop ? 'displayLg' : 'headingLg'} style={{ marginTop: space.s6, textAlign: 'center' }}>
          {t('onb_pending_title')}
        </AppText>
        <AppText variant="bodyMd" tone="secondary" style={{ marginTop: space.s2, textAlign: 'center', maxWidth: 420, lineHeight: 22 }}>
          {t('onb_pending_body')}
        </AppText>

        <SummaryCard flatCode={resolvedFlatCode} societyName={societyName} residentType={residentType} submittedAt={submittedAt} />

        {/* TODO(wire): no push-notification-preferences endpoint yet — visual only. */}
        <Button
          title={t('onb_pending_notify')}
          kind="secondary"
          style={{ marginTop: space.s5 }}
          onPress={() => {}}
        />

        {!desktop ? (
          <Button
            title={t('onb_sign_out')}
            kind="tertiary"
            loading={signingOut}
            onPress={() => { void handleSignOut(); }}
            style={{ marginTop: space.s4 }}
          />
        ) : null}
      </View>
    </Screen>
  );
}
